import {
  BlockTitle,
  Button,
  EmptyState,
  Skeleton,
  StatusTag,
  Surface,
  formatCurrency,
  formatDate,
  formatPhone,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { openSignedUrl, usePdfLink } from '@/features/documents/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  saveDownloadedFile,
  type ProofSignature,
  type SignatureDetail,
  type SignatureProvider,
  type SignatureDocument,
  useSignature,
  useSignedPdf,
} from './api'
import {
  CHANNEL_IN_SENTENCE,
  MODE_LABELS,
  ROLE_LABELS,
  TIMELINE_LABELS,
  daysOpenText,
  deliveryWord,
  documentsWord,
  formatDateTime,
  providerClinicSignerLine,
  providerSentLine,
} from './labels'
import { ManualLinkActions } from './manual-link-actions'
import { manualMessageOf, useSignatureActions, type ActionTarget } from './use-signature-actions'

/**
 * O painel de um envio (docs/api/assinaturas.md, GET /signatures/:appointmentId): o que foi enviado, por
 * onde, o que a paciente já fez, as assinaturas com a prova, a linha do tempo e as ações deste perfil.
 */

const line = 'font-body text-secondary text-[12px] leading-[1.6] font-light'
const value = 'font-body text-ink text-[13px] leading-[1.6] font-light'

function PanelSkeleton() {
  return (
    <div role="status" aria-label="Carregando o envio" className="flex flex-col gap-4">
      <Skeleton className="h-[11px] w-[120px]" />
      <Skeleton className="h-[20px] w-[220px]" />
      <Skeleton className="h-[60px] w-full" />
      <Skeleton className="h-[120px] w-full" />
    </div>
  )
}

function Proof({ signature }: { signature: ProofSignature }) {
  const who =
    signature.role === 'clinic_signer'
      ? `${ROLE_LABELS.clinic_signer}${signature.signerRole ? ` · ${signature.signerRole === 'practitioner' ? 'prestadora' : signature.signerRole === 'root' ? 'Westhetics' : 'gestora'}` : ''}`
      : ROLE_LABELS[signature.role]
  return (
    <li className="border-hairline border-t py-3 first:border-t-0">
      <div className="font-display text-ink text-[14px] leading-[1.3]">
        {titleCase(signature.signer.name)}
      </div>
      <div className={line}>
        {who} · {MODE_LABELS[signature.mode]}
      </div>
      <div className={line}>
        {formatDateTime(signature.signedAt)}
        {signature.ip ? ` · IP ${signature.ip}` : ''}
        {signature.device ? ` · ${signature.device}` : ''}
      </div>
      {signature.signer.docNumber ? (
        <div className={line}>CPF {signature.signer.docNumber}</div>
      ) : null}
      {signature.drawnSignature ? (
        <img
          src={signature.drawnSignature.fileUrl}
          alt={`Traço de ${titleCase(signature.signer.name)}`}
          className="border-hairline-strong mt-2 max-h-[64px] w-auto border-b"
        />
      ) : null}
      {signature.hash ? (
        <div className={`${line} break-all`}>
          Hash {signature.hash.slice(0, 8)}…{signature.hash.slice(-6)}
        </div>
      ) : null}
    </li>
  )
}

function DocumentRow({
  detail,
  doc,
  onDelete,
}: {
  detail: SignatureDetail
  doc: SignatureDocument
  onDelete?: (doc: SignatureDocument) => void
}) {
  const signed = useSignedPdf()
  const pdf = usePdfLink()
  const [busy, setBusy] = useState(false)
  function download() {
    setBusy(true)
    if (doc.status === 'signed') {
      signed.mutate(
        { appointmentId: detail.appointmentId, documentId: doc.id },
        {
          onSuccess: (file) => saveDownloadedFile(file, `${doc.title}.pdf`),
          onError: (error) => toast(errorMessage(error)),
          onSettled: () => setBusy(false),
        },
      )
      return
    }
    pdf.mutate(
      { appointmentId: detail.appointmentId, documentId: doc.id },
      {
        onSuccess: (link) => openSignedUrl(link.url),
        onError: (error) => toast(errorMessage(error)),
        onSettled: () => setBusy(false),
      },
    )
  }
  const marks = [
    doc.readAt ? `lido em ${formatDateTime(doc.readAt)}` : null,
    doc.patientSignedAt && !doc.signedAt
      ? `assinado pela paciente em ${formatDateTime(doc.patientSignedAt)}`
      : null,
    doc.signedAt ? `assinado em ${formatDateTime(doc.signedAt)}` : null,
  ].filter(Boolean)
  return (
    <li className="border-hairline flex flex-wrap items-start justify-between gap-2 border-t py-3 first:border-t-0">
      <div className="min-w-0 flex-1">
        <div className="font-display text-ink text-[14px] leading-[1.3]">{doc.title}</div>
        <div className={line}>{marks.length > 0 ? marks.join(' · ') : 'ainda não lido'}</div>
      </div>
      <div className="flex items-center gap-3">
        <StatusTag level={doc.level}>{doc.label}</StatusTag>
        {doc.hasPdf && !doc.isRestricted ? (
          <Button
            variant="text"
            size="sm"
            loading={busy}
            loadingLabel="Preparando…"
            onClick={download}
          >
            {doc.status === 'signed' ? 'PDF assinado' : 'PDF'}
          </Button>
        ) : null}
        {onDelete && doc.status === 'signed' ? (
          <Button variant="text" size="sm" onClick={() => onDelete(doc)}>
            Excluir…
          </Button>
        ) : null}
      </div>
    </li>
  )
}

export function SignaturePanel({
  appointmentId,
  whatsappMode,
  provider,
  onClose,
}: {
  appointmentId: string
  whatsappMode: 'provider' | 'manual'
  /** O provedor da clínica ativa; o envio pode ter nascido em outro (RN-ASS-33). */
  provider?: SignatureProvider
  onClose: () => void
}) {
  const query = useSignature(appointmentId)
  const actions = useSignatureActions({ whatsappMode, provider })

  return (
    <Surface className="max-desk:px-4" aria-label="Envio">
      <div className="flex items-start justify-between gap-3">
        <div className="eyebrow">Envio</div>
        <Button variant="text" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </div>
      {query.isPending ? (
        <div className="mt-4">
          <PanelSkeleton />
        </div>
      ) : query.isError ? (
        <LoadError
          className="mt-4"
          title="Não conseguimos carregar o envio"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <PanelBody detail={query.data} actions={actions} />
      )}
      {actions.dialogs}
    </Surface>
  )
}

function PanelBody({
  detail,
  actions,
}: {
  detail: SignatureDetail
  actions: ReturnType<typeof useSignatureActions>
}) {
  const request = detail.signatureRequest
  const isAutentique = (request?.provider ?? detail.clinic.signatureProvider) === 'autentique'
  const target: ActionTarget = {
    appointmentId: detail.appointmentId,
    patientName: detail.patient.name,
    contact: { whatsapp: detail.patient.whatsapp, email: detail.patient.email },
    status: detail.status,
    actions: detail.actions,
    documentCount: detail.documents.length,
    clinicName: detail.clinic.name,
    provider: request?.provider ?? detail.clinic.signatureProvider,
  }
  const manual = request ? manualMessageOf(request, detail.patient.name) : null
  const can = (action: string) => detail.actions.includes(action as never)
  const pending = request?.pendingAction

  return (
    <div className="mt-3">
      <div className="font-display text-ink text-[20px] leading-[1.2] font-light">
        <Link to={routes.patientTab(detail.patient.id, 'documents')} className="hover:opacity-80">
          {titleCase(detail.patient.name)}
        </Link>
      </div>
      <div className={`${line} mt-1`}>
        {[
          detail.procedure,
          detail.practitioner ? titleCase(detail.practitioner.name) : null,
          detail.totalAmountCents !== null ? formatCurrency(detail.totalAmountCents / 100) : null,
          request ? `protocolo ${request.protocolNumber}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <StatusTag level={detail.level}>{detail.label}</StatusTag>
        {detail.daysOpen !== null ? (
          <span className={detail.isUrgent ? 'font-body text-plum text-[12px]' : line}>
            {daysOpenText(detail.daysOpen)} em aberto
          </span>
        ) : null}
        {pending ? (
          <span className={line}>
            {pending.kind === 'cancel'
              ? 'cancelando…'
              : pending.kind === 'resend'
                ? 'reenviando…'
                : 'enviando…'}
          </span>
        ) : null}
      </div>

      {detail.actions.length > 0 ? (
        <div className="mt-5 flex flex-wrap gap-3">
          {can('sign') ? (
            <Button size="sm" onClick={() => actions.askSign(target)}>
              Assinar como responsável
            </Button>
          ) : null}
          {can('send') ? (
            <Button size="sm" asChild>
              <Link
                to={
                  detail.type === 'procedure'
                    ? routes.generateDocuments(detail.patient.id, detail.appointmentId)
                    : routes.standaloneDocuments(detail.patient.id, detail.appointmentId)
                }
              >
                Enviar pela etapa 3
              </Link>
            </Button>
          ) : null}
          {can('resend') ? (
            <Button variant="outline" size="sm" onClick={() => actions.askResend(target)}>
              Reenviar
            </Button>
          ) : null}
          {can('cancel') ? (
            <Button variant="text" size="sm" onClick={() => actions.askCancel(target)}>
              Cancelar envio
            </Button>
          ) : null}
        </div>
      ) : null}

      {isAutentique && detail.status === 'awaiting_clinic_signature' && !can('sign') ? (
        <div className="border-lilac bg-surface mt-5 border-l-2 px-4 py-3">
          <div className={value}>{providerClinicSignerLine(detail.clinic.legalEmail)}</div>
        </div>
      ) : null}

      {isAutentique && request?.link && can('copy_link') ? (
        <div className="border-lilac bg-surface mt-5 border-l-2 px-4 py-3">
          <div className={value}>
            A paciente lê e assina na Autentique. Se ela não achar a mensagem, copie o link e mande.
          </div>
          <div className="mt-3">
            <Button
              variant="text"
              size="sm"
              onClick={() => void actions.copyLink(request.link ?? '')}
            >
              Copiar link
            </Button>
          </div>
        </div>
      ) : null}

      {manual && can('copy_link') ? (
        <div className="border-lilac bg-surface mt-5 border-l-2 px-4 py-3">
          <div className={value}>
            O WhatsApp ainda não sai pelo sistema: mande a mensagem pronta pelo seu WhatsApp.
          </div>
          <div className="mt-3">
            <ManualLinkActions
              message={manual}
              onOpen={actions.openWhatsapp}
              onCopyLink={(link) => void actions.copyLink(link)}
              onCopyMessage={(text) => void actions.copyMessage(text)}
            />
          </div>
        </div>
      ) : null}

      {request ? (
        <section className="mt-6" aria-label="Entrega">
          <BlockTitle title="Entrega" />
          <div className={`${value} mt-2`}>
            {isAutentique
              ? `${providerSentLine(request.channels)} em ${formatDateTime(request.sentAt)}`
              : `Enviado em ${formatDateTime(request.sentAt)} por ${CHANNEL_IN_SENTENCE[request.channels]}`}
            {request.createdBy ? ` · ${titleCase(request.createdBy.name)}` : ''}
          </div>
          <ul className="mt-1">
            {request.delivery.map((d) => (
              <li key={d.channel} className={line}>
                {d.channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'} · {deliveryWord(d)}
                {d.at && d.status !== 'queued' && d.status !== 'manual'
                  ? ` em ${formatDateTime(d.at)}`
                  : ''}
                {d.detail && d.status === 'failed' ? ` · ${d.detail}` : ''}
              </li>
            ))}
            {request.delivery.length === 0 ? (
              <li className={line}>Nenhuma mensagem saiu ainda.</li>
            ) : null}
          </ul>
          <div className={`${line} mt-1`}>
            {request.recipient.role === 'guardian'
              ? `Responsável legal: ${titleCase(request.recipient.name)} · `
              : ''}
            {request.recipient.whatsapp ? `${formatPhone(request.recipient.whatsapp)} · ` : ''}
            {request.recipient.email ?? 'sem e-mail'}
            {' · '}
            {request.linkExpiresAt && detail.status === 'awaiting_patient'
              ? `link vale até ${formatDate(new Date(request.linkExpiresAt))}`
              : request.linkOpenedAt
                ? `abriu o link em ${formatDateTime(request.linkOpenedAt)}`
                : 'ainda não abriu o link'}
            {request.resendCount > 0
              ? ` · ${request.resendCount} ${request.resendCount === 1 ? 'reenvio' : 'reenvios'}`
              : ''}
          </div>
          {request.cancellationReason ? (
            <div className={`${line} mt-1`}>
              Motivo do cancelamento: {request.cancellationReason}
            </div>
          ) : null}
        </section>
      ) : (
        <EmptyState
          className="mt-6"
          title="Ainda não enviado"
          description={`${documentsWord(detail.documents.length)} gerados e prontos. Envie pela etapa 3 de gerar documentos.`}
        />
      )}

      <section className="mt-6" aria-label="Documentos">
        <BlockTitle title="Documentos" />
        <ul className="mt-2">
          {detail.documents.map((doc) => (
            <DocumentRow
              key={doc.id}
              detail={detail}
              doc={doc}
              onDelete={
                can('delete')
                  ? (d) => actions.askDelete({ ...target, document: { id: d.id, title: d.title } })
                  : undefined
              }
            />
          ))}
        </ul>
      </section>

      {detail.signatures.length > 0 ? (
        <section className="mt-6" aria-label="Assinaturas">
          <BlockTitle title="Assinaturas" />
          <ul className="mt-2">
            {detail.signatures.map((s) => (
              <Proof key={s.id} signature={s} />
            ))}
          </ul>
        </section>
      ) : null}

      {detail.timeline.length > 0 ? (
        <section className="mt-6" aria-label="Linha do tempo">
          <BlockTitle title="Linha do tempo" />
          <ol className="mt-2">
            {detail.timeline.map((item, index) => (
              <li
                key={`${item.at}-${index}`}
                className="border-hairline flex gap-3 border-t py-2 first:border-t-0"
              >
                <span className={`${line} w-[120px] shrink-0`}>{formatDateTime(item.at)}</span>
                <span className={value}>
                  {TIMELINE_LABELS[item.type] ?? item.type}
                  {item.channel ? ` · ${item.channel === 'whatsapp' ? 'WhatsApp' : 'e-mail'}` : ''}
                  {item.userName ? ` · ${titleCase(item.userName)}` : ''}
                  {item.source === 'integration'
                    ? ` · por ${item.integrationName ?? 'app de integração'}`
                    : ''}
                  {item.detail && item.type !== 'send_requested' && item.type !== 'resent'
                    ? ` · ${item.detail}`
                    : ''}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </div>
  )
}
